import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getAllEntitiesApply,
  getEntitiesCount,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import { createRequestDataSource, createRequestsStatusOperator, initializeAsPending, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { NotificationParameterFlags, TypeNotification } from '@timechef/core/api';
import { NotificationDto } from '@timechef/core/api/models/notification-dto';
import { NotificationSettingsByFlag } from '@timechef/core/api/models/notification-settings-by-flag';
import { capacitorStorageStrategy, includeKeys, selectAppRequestStatus } from '@timechef/core/utils/elf-utils';
import { map } from 'rxjs';
import { PageInfo } from './notifications.models';
import { computeNotifications } from './notifications.utils';

export interface NotificationsProps {
  notificationsPageInfo: PageInfo;
  userSettings: NotificationSettingsByFlag[];
}

export enum NotificationStatusKey {
  Notification = 'notifications',
  NotificationSettings = 'notification-settings',
  UpdateSettings = 'update-settings',
  DeleteNotification = 'delete-notification',
}

@Injectable({ providedIn: 'root' })
export class NotificationsRepository {
  private store = createStore(
    { name: 'notification' },
    withProps<NotificationsProps>({
      notificationsPageInfo: {
        hasMore: false,
        currentPageIndex: 0,
      },
      userSettings: [],
    }),
    withEntities<NotificationDto>(),
    withRequestsStatus(initializeAsPending(NotificationStatusKey.Notification)),
  );

  instance = persistState(this.store, {
    key: 'notification',
    storage: capacitorStorageStrategy,
    source: () => this.store.pipe(includeKeys(['entities', 'ids'])),
  });

  notificationsByPeriodDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      this.store.pipe(
        selectAllEntities(),
        map((notifications) => computeNotifications(new Date(), notifications)),
      ),
    requestKey: NotificationStatusKey.Notification,
    dataKey: 'data',
    idleAsPending: true,
  });

  notificationSettingsDS = createRequestDataSource({
    store: this.store,
    data$: () => this.store.pipe(select((s) => s.userSettings)),
    requestKey: NotificationStatusKey.NotificationSettings,
    dataKey: 'data',
    idleAsPending: true,
  });

  notificationsPageInfo$ = this.store.pipe(select((s) => s.notificationsPageInfo));
  updateNotificationSettingsRequestStatus$ = (category: NotificationParameterFlags, type: TypeNotification) =>
    this.store.pipe(selectAppRequestStatus(buildUpdateSettingsKey(category, type)));

  updateSettingsErrorTracker$ = this.store.pipe(selectAppRequestStatus(NotificationStatusKey.UpdateSettings));

  getNotificationsRequestStatus = () => this.store.query((s) => s.requestsStatus[NotificationStatusKey.Notification]);
  getNotificationsCount = () => this.store.query(getEntitiesCount());
  getUnreadNotificationIds = () =>
    this.store.query(
      getAllEntitiesApply({
        mapEntity: (notification) => notification.id,
        filterEntity: (notification) => !notification.read,
      }),
    );

  getNotificationSettings = () => this.store.query((s) => s.userSettings);

  addNotifications(notifications: NotificationDto[], hasMore: boolean, currentPageIndex: number) {
    const clear = currentPageIndex === 0;
    notifications
      ? this.store.update(
          setProps({ notificationsPageInfo: { hasMore, currentPageIndex } }),
          clear ? setEntities(notifications) : addEntities(notifications),
          this.notificationsByPeriodDS.setSuccess(),
        )
      : this.store.update(setProps({ notificationsPageInfo: { hasMore, currentPageIndex } }), this.notificationsByPeriodDS.setSuccess());
  }

  deleteNotification(notificationId: number): void {
    this.store.update(deleteEntities(notificationId));
  }

  updateNotificationSettings(settings: NotificationSettingsByFlag[]) {
    this.store.update((s) => ({ ...s, userSettings: settings }), this.notificationSettingsDS.setSuccess());
  }

  updateNotificationSetting(category: NotificationParameterFlags, type: TypeNotification, enable: boolean) {
    //retrieve old setting value
    const userSetting = this.store.query((s) => s.userSettings);
    //find the setting to be updated with his type and category, and update it
    const categorySetting = userSetting.find((e) => e.category === category);
    if (categorySetting) {
      switch (type) {
        case TypeNotification.Push:
          categorySetting.pushEnabled = enable;
          break;
        case TypeNotification.Email:
          categorySetting.emailEnabled = enable;
          break;
        case TypeNotification.Sms:
          categorySetting.smsEnabled = enable;
          break;
      }
      //replace the value stored with the new updated value
      this.store.update(
        (s) => ({ ...s, userSettings: userSetting }),
        updateRequestStatus(NotificationStatusKey.UpdateSettings, 'success'),
        updateRequestStatus(buildUpdateSettingsKey(category, type), 'success'),
      );
    }
  }

  markNotificationsAsRead(ids: number[]) {
    ids.forEach((id) => {
      this.store.update(updateEntities(id, { read: true }));
    });
  }

  public trackRequestStatus = createRequestsStatusOperator(this.store);
}

export const buildUpdateSettingsKey = (category: NotificationParameterFlags, type: TypeNotification) =>
  `${NotificationStatusKey.UpdateSettings}_${category}_${type}`;
